/**
 * LeadDocumentsPanel
 *
 * One self-contained panel for UAE document storage on a single lead.
 * Uploads (Emirates ID, Passport, SPA, NOC, cheque copy, …) live on the
 * shared `broker_sales` row, so this same panel can be embedded in:
 *
 *   - the Brokerage Hub lead drawer / edit modal (read + write)
 *   - the Indirect Sales lead row (read-only, when `readOnly` is true)
 *
 * Both surfaces see the same files because they read from the same lead
 * document — that is the whole point of the common-lead-id design.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import {
  Upload, FileText, Trash2, Download, Loader2, FileWarning,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import {
  UAE_DOC_TYPES,
  labelForDocType,
  fetchLeadDocuments,
  uploadLeadDocument,
  deleteLeadDocument,
  leadDocumentDownloadUrl,
} from '../api/brokerages';

const fmtSize = (bytes) => {
  const n = Number(bytes || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—');

// Visually group "people docs" (KYC) vs. "deal docs" — same colour family
// the rest of the Hub uses so the panel doesn't feel bolted on.
const PILL_TONE = {
  emirates_id: 'bg-violet-50 text-violet-700 border-violet-200',
  passport: 'bg-violet-50 text-violet-700 border-violet-200',
  visa: 'bg-violet-50 text-violet-700 border-violet-200',
  passport_photo: 'bg-violet-50 text-violet-700 border-violet-200',
  trade_license: 'bg-amber-50 text-amber-700 border-amber-200',
  poa: 'bg-amber-50 text-amber-700 border-amber-200',
  title_deed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  spa: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  booking_form: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  noc: 'bg-sky-50 text-sky-700 border-sky-200',
  ejari: 'bg-sky-50 text-sky-700 border-sky-200',
  cheque: 'bg-rose-50 text-rose-700 border-rose-200',
  salary_certificate: 'bg-slate-50 text-slate-700 border-slate-200',
  bank_statement: 'bg-slate-50 text-slate-700 border-slate-200',
  other: 'bg-slate-50 text-slate-700 border-slate-200',
};

export default function LeadDocumentsPanel({ leadId, readOnly = false, compact = false }) {
  const { token } = useAuth();
  const fileInputRef = useRef(null);

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('emirates_id');
  const [label, setLabel] = useState('');
  const [pickedFile, setPickedFile] = useState(null);

  const reload = useCallback(async () => {
    if (!token || !leadId) return;
    setLoading(true);
    try {
      const data = await fetchLeadDocuments(token, leadId);
      setDocs(data?.documents || []);
    } catch (e) {
      // Empty list isn't a hard failure — a lead may have zero docs.
      console.warn('[LeadDocumentsPanel] reload failed', e);
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [token, leadId]);

  useEffect(() => { reload(); }, [reload]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    setPickedFile(f || null);
  };

  const onUpload = async () => {
    if (!pickedFile) {
      toast.error('Pick a file first');
      return;
    }
    if (!docType) {
      toast.error('Select a document type');
      return;
    }
    setUploading(true);
    try {
      await uploadLeadDocument(token, leadId, pickedFile, docType, label);
      toast.success(`${labelForDocType(docType)} uploaded`);
      setPickedFile(null);
      setLabel('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (docId, filename) => {
    if (!window.confirm(`Remove "${filename}"?`)) return;
    try {
      await deleteLeadDocument(token, leadId, docId);
      toast.success('Document removed');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // Group docs by type so a lead with all 7 UAE docs reads naturally —
  // KYC docs together, deal docs together. Within a group, newest first
  // (the API already sorts that way).
  const grouped = useMemo(() => {
    const m = new Map();
    for (const d of docs) {
      const key = d.doc_type || 'other';
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(d);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [docs]);

  return (
    <div className="space-y-3">
      {!readOnly && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[180px] flex-1">
              <label className="mb-1 block text-xs font-medium text-slate-600">Type</label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="h-9 bg-white">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {UAE_DOC_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[180px] flex-1">
              <label className="mb-1 block text-xs font-medium text-slate-600">
                Label <span className="text-slate-400">(optional)</span>
              </label>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. EID front, primary applicant"
                className="h-9 bg-white"
              />
            </div>
            <div className="min-w-[200px] flex-1">
              <label className="mb-1 block text-xs font-medium text-slate-600">File</label>
              <input
                ref={fileInputRef}
                type="file"
                onChange={onPick}
                accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx"
                className="block w-full text-sm text-slate-600 file:mr-2 file:rounded-md file:border file:border-slate-200 file:bg-white file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-slate-700 hover:file:bg-slate-50"
              />
            </div>
            <Button
              onClick={onUpload}
              disabled={uploading || !pickedFile}
              className="h-9 bg-violet-600 hover:bg-violet-700"
            >
              {uploading
                ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Uploading…</>
                : <><Upload size={14} className="mr-1.5" /> Upload</>}
            </Button>
          </div>
          <p className="mt-2 text-[11px] text-slate-500">
            Max 20 MB · PDF / image / DOC. Files are visible from the Indirect Sales
            dashboard automatically.
          </p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 size={14} className="animate-spin" /> Loading documents…
        </div>
      ) : docs.length === 0 ? (
        <div className="flex items-center gap-2 rounded-md border border-dashed border-slate-300 bg-white px-3 py-4 text-sm text-slate-500">
          <FileWarning size={16} className="text-slate-400" />
          No documents on file yet.
        </div>
      ) : (
        <div className="space-y-2">
          {grouped.map(([type, items]) => (
            <div key={type} className="rounded-md border border-slate-200 bg-white">
              <div className="flex items-center justify-between border-b border-slate-100 px-3 py-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                  {labelForDocType(type)}
                </span>
                <Badge variant="outline" className={`${PILL_TONE[type] || PILL_TONE.other} text-[10px]`}>
                  {items.length} file{items.length === 1 ? '' : 's'}
                </Badge>
              </div>
              <ul className="divide-y divide-slate-100">
                {items.map((d) => (
                  <li key={d.id} className="flex items-center gap-2 px-3 py-2">
                    <FileText size={15} className="shrink-0 text-slate-400" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-800">
                        {d.filename}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {d.label ? <>{d.label} · </> : null}
                        {fmtSize(d.size_bytes)} · {fmtDate(d.uploaded_at)}
                        {d.uploaded_by_name ? <> · {d.uploaded_by_name}</> : null}
                      </div>
                    </div>
                    <a
                      href={leadDocumentDownloadUrl(token, leadId, d.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex h-7 items-center gap-1 rounded-md border border-slate-200 bg-white px-2 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
                    >
                      <Download size={12} /> Open
                    </a>
                    {!readOnly && (
                      <button
                        onClick={() => onDelete(d.id, d.filename)}
                        className="inline-flex h-7 items-center justify-center rounded-md border border-rose-200 bg-white px-2 text-rose-600 hover:bg-rose-50"
                        title="Remove document"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {!readOnly && !compact && docs.length > 0 && (
        <p className="text-[11px] text-slate-500">
          Tip: documents stay attached to the lead even if the broker, agent
          or pipeline stage changes.
        </p>
      )}
    </div>
  );
}
